/** @format */

import React, { FC, useEffect, useState, useMemo } from 'react'
import styled from 'styled-components'
import { TabsListProps, getRulesUnits } from './constants'
import { useAsyncFn } from 'react-use'
import TablePage from 'src/components/TablePage'
import {
  Dialog,
  _$,
  Icon,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Checkbox,
  Table,
  TableColumn
} from '@king-design/react'
import { useHistory } from 'react-router'
import DeleteConfirm from 'src/components/DeleteConfirm'
import BaseChart from 'src/components/BaseChart'
import API from 'src/api/ApiAlarmHistory'
import dayjs from 'dayjs'
import useFormTable from 'src/hooks/useFormTable'
import useTableGroup from 'src/hooks/useTableGroup'
// eslint-disable-next-line max-len
import CloudListTableCopyTips from 'src/containers/MonitorCloud/CloudListTable/CloudListTableCopyTips'
import { set, cloneDeep } from 'lodash'
import { calcDateFormat } from 'src/containers/MonitorCloud/constants'
import NoDataComponent from 'src/components/NoData'
import { request, tooltipFormatter, handleUnit } from 'src/utils'
import { tableScheme, initSchemeValue, reciverScheme } from './constants'
import { mbpsproduct } from 'src/containers/MonitorCreate'
import ViewDrawer from 'src/containers/MonitorAlarmSilences/ViewDrawer'

const TacticsList: FC<TabsListProps> = ({
  className,
  region,
  dateTime,
  query,
  policyId,
  policyName,
  queryType
}) => {
  const history = useHistory()
  const productGroupList = useTableGroup('productList')
  const [tableGroup, setTableGroup] = useState({
    productType: 999
  })
  const [showChart, setShowChart] = useState(false)
  // const [dropdownValue, setDropdownValue] = useState<boolean>(false)
  const [schemeValue, setSchemeValue] = useState<string[]>(initSchemeValue)
  // 屏蔽规则
  const [shieldVersion, setShieldVersion] = useState<string>('')
  const { run, result, loading, pageState, total }: any = useFormTable(
    API.getTacticsHistoryList,
    {
      params: {
        Region: region,
        StartTime: dateTime.StartTime,
        EndTime: dateTime.EndTime,
        PolicyName: policyName ? policyName : queryType === 'PolicyName' ? query : '',
        PolicyId: policyId,
        InstanceId: queryType === 'InstanceId' ? query : '',
        ProductType: tableGroup.productType
      },
      manual: true
    },
    data => {
      return {
        tableData: data?.alertHistoryList,
        totalCount: data?.totalCount
      }
    }
  )

  const [chartData, getChartData] = useAsyncFn(async item => {
    const { queryData } = item || {}
    const tags = item.queryData.tags === '' ? '' : JSON.parse(item.queryData.tags)
    const params = {
      aggregator: queryData?.function,
      end: queryData.endTime,
      instance: item.instanceId,
      interval: queryData.interval,
      lines: [
        {
          metric: item.queryData.metricname,
          instance: item.instanceId,
          tags: tags,
          producttype: item.productType,
          legend: item.queryData.legend
        }
      ],
      start: queryData.startTime
    }

    const {
      data: { data }
    } = await request(API.getTacticsHistorydashboard, {
      method: 'post',
      data: params,
      region: item.region
    })
    if (data && data.series && data.series.length) {
      let { units } = getRulesUnits(item.rule, item.curValue, item.productType)
      if (!mbpsproduct.includes(parseInt(item.productType)) && ['Kbps', 'Mbps'].includes(units)) {
        units = 'bps'
      }
      const xData = data.series[0].data.map(([time]) =>
        dayjs(time * 1000).format(calcDateFormat([params.start, params.end], params.interval))
      )
      const series = []
      let maxValue = 0
      let minValue = 0
      data.series.forEach(({ data, name }) => {
        const symbol = data.find(([_, count]) => count === null)
        const seriesObject = {
          name,
          type: 'line',
          symbol: data.length > 5 && !symbol ? 'none' : 'emptyCircle',
          data: data.map(([_, count]) => count),
          showSymbol: false,
          lineStyle: {
            width: 2
          }
        }
        series.push(seriesObject)
        const max = Math.max.apply(null, seriesObject.data)
        maxValue = max > maxValue ? max : maxValue
        const min = Math.min.apply(null, seriesObject.data)
        minValue = min > minValue ? min : minValue
      })
      return {
        xData,
        series,
        maxValue,
        minValue,
        interval: params.interval,
        units: units || ''
      }
    } else {
      return undefined
    }
  })
  useEffect(() => {
    if (dateTime?.StartTime && dateTime?.EndTime && region) {
      const params = {
        PageIndex: 1,
        StartTime: dateTime.StartTime,
        EndTime: dateTime.EndTime,
        InstanceId: queryType === 'InstanceId' ? query : ''
      }
      if (policyName) {
        set(params, 'PolicyName', policyName)
      } else {
        set(params, 'PolicyName', queryType === 'PolicyName' ? query : '')
      }
      policyId && set(params, 'PolicyId', policyId)
      run(params)
    }
  }, [dateTime.StartTime, dateTime.EndTime, region, query, queryType])

  // 渲染产品类型
  const renderProductType = type => {
    const item = productGroupList.find(i => i?.value === type)
    return item?.label || type
  }

  const TacticsColumns = {
    policyName: {
      title: _$('策略名称'),
      width: 200,
      template: item => (
        <a
          onClick={() => {
            history.push(`/monitor/alarmServer/rule/detail/${item.policyId}`)
          }}
        >
          {item.policyName}
        </a>
      )
    },
    productType: {
      title: _$('产品类型'),
      width: 150,
      group: policyName ? undefined : productGroupList,
      template: item => <span>{renderProductType(item.productType)}</span>
    },
    instanceName: {
      title: _$('故障资源'),
      width: 150,
      template: item => (
        <div className="table_instance">
          <span>{item.instanceName}</span>
          <CloudListTableCopyTips instanceId={item.instanceId} />
        </div>
      )
    },
    rule: {
      title: _$('告警规则'),
      width: 300,
      template: item => {
        const rule = item.rule
          .replace(/LessThanLastPeriod/g, _$('周期环比下降'))
          .replace(/GreaterThanLastPeriod/g, _$('周期环比上涨'))
        const tags = item.queryData.tags === '' ? '' : JSON.parse(item.queryData.tags)
        const tagKeys = Object.keys(tags)?.filter(tag => {
          return tag !== 'host'
        })
        return (
          <div className="table-rule">
            <p className="text_margin">
              <b>规则：</b>
              {rule}
            </p>
            {tagKeys?.length ? (
              <p className="text_margin">
                <b>维度：</b>
                {tagKeys
                  .map(key => {
                    const value = tags[key]
                    return `${key}${value ? `=${value}` : ''}`
                  })
                  .join('、')}
              </p>
            ) : null}
          </div>
        )
      }
    },
    curValue: {
      title: _$('触发值'),
      width: 100,
      template: item => {
        const { units, triggerValue } = getRulesUnits(item.rule, item.curValue, item.productType)
        return (
          <span>
            {triggerValue || 0}
            {units || ''}
          </span>
        )
      }
    },
    triggerTime: {
      width: 100,
      title: _$('发生时间')
    },
    duration: {
      width: 80,
      title: _$('持续时间')
    },
    reciver: {
      title: _$('通知对象'),
      width: 120,
      template: item => (
        <div className="ksc-space">
          {item.alarmReceivers ? (
            <DeleteConfirm
              content={
                <>
                  <TablePage
                    tableProps={{
                      checkType: 'none',
                      scheme: reciverScheme,
                      data: item.alarmReceivers
                    }}
                  ></TablePage>
                </>
              }
            >
              <a>{_$('联系人')}</a>
            </DeleteConfirm>
          ) : null}
          <DeleteConfirm
            content={
              <Table data={item?.callBackResult || []} checkType="none">
                <TableColumn key="callBack" title="回调地址" />
                <TableColumn
                  key="result"
                  title="结果"
                  width={200}
                  slotTemplate={([data]) => {
                    if (data.result === 'success') {
                      return (
                        <div className="ksc-space">
                          <Icon
                            className="ion-checkmark-circled"
                            color="success"
                            style={{ marginRight: '4px' }}
                          />
                          成功
                        </div>
                      )
                    } else if (data.result === '') {
                      return (
                        <div className="ksc-space">
                          <Icon
                            className="k-icon-warning-fill"
                            color="warning"
                            style={{ marginRight: '4px' }}
                          />
                          未发送
                        </div>
                      )
                    } else {
                      return (
                        <div style={{ alignItems: 'flex-start' }} className="ksc-space">
                          <Icon
                            className="k-icon-error-fill"
                            color="danger"
                            style={{ marginRight: '4px' }}
                          />
                          <p style={{ width: '160px', wordBreak: 'break-all' }}>
                            {data.result || ''}
                          </p>
                        </div>
                      )
                    }
                  }}
                />
              </Table>
            }
          >
            <a>{_$('回调')}</a>
          </DeleteConfirm>
        </div>
      )
    },
    alarmState: {
      title: _$('状态'),
      width: 80,
      template: item => {
        if (item.alarmState === 1) {
          return <span>{_$('告警发生')}</span>
        } else if (item.alarmState === 0) {
          return <span>{_$('告警恢复')}</span>
        } else if (item.alarmState === 3) {
          return (
            <a
              onClick={() => {
                setShieldVersion(item.shieldVersion)
              }}
            >
              {_$('已屏蔽')}
            </a>
          )
        } else {
          return ''
        }
      }
    },
    action: {
      title: (
        <div className="thead-action">
          {_$('操作')}
          <Dropdown
            trigger="click"
            className="tactics-history-action"
            position={{ my: 'right top+5' }}
          >
            <Icon className="ion-gear-b cursor-pointer" />
            <DropdownMenu key="all">
              <DropdownItem hideOnSelect={false}>
                <Checkbox
                  indeterminate={schemeValue.length > 0 && schemeValue.length < tableScheme.length}
                  value={schemeValue.length === tableScheme.length}
                  onChangeValue={checked => {
                    if (checked) {
                      setSchemeValue(tableScheme.map(({ value }) => value))
                    } else {
                      const filter = tableScheme
                        .filter(({ disabled }) => disabled)
                        .map(({ value }) => value)
                      setSchemeValue([...filter])
                    }
                  }}
                >
                  全选
                </Checkbox>
              </DropdownItem>
              {tableScheme.map(({ value, label, disabled }) => (
                <DropdownItem key={value} hideOnSelect={false}>
                  <Checkbox
                    name="scheme"
                    trueValue={value}
                    value={schemeValue}
                    disabled={disabled}
                    onChangeValue={values => setSchemeValue(values)}
                  >
                    {label}
                  </Checkbox>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
      ),
      width: 80,
      template: item => {
        return (
          <a
            onClick={() => {
              getChartData(item)
              setShowChart(true)
            }}
          >
            {_$('图表')}
          </a>
        )
      }
    }
  }

  const handleScheme = useMemo(() => {
    if (productGroupList?.length) {
      const result = {}
      Object.keys(TacticsColumns).forEach(schemeKey => {
        if (schemeValue.includes(schemeKey)) {
          result[schemeKey] = cloneDeep(TacticsColumns[schemeKey])
        }
      })
      return result
    }
    return undefined
  }, [schemeValue, productGroupList])

  return (
    <div className={className}>
      {handleScheme ? (
        <TablePage
          tableProps={{
            checkType: 'none',
            rowKey: row => row.historyId,
            resizable: true,
            scheme: handleScheme,
            minColWidth: 100,
            data: Array.isArray(result) ? result : [],
            group: tableGroup,
            onChangeGroup: value => {
              setTableGroup(value)
              run({
                PageIndex: 1,
                ProductType: value.productType !== '' ? value.productType : 999,
                Query: policyName
              })
            }
          }}
          pageProps={{ total, ...pageState }}
          loading={loading}
          run={run}
        />
      ) : null}
      <Dialog
        mode="destroy"
        title={_$('历史数据趋势')}
        width={1096}
        value={showChart}
        onChangeValue={show => {
          setShowChart(show)
        }}
      >
        <div style={{ height: '300px' }}>
          {chartData?.value?.xData?.length && chartData?.value?.series?.length ? (
            <BaseChart
              options={{
                xAxis: {
                  type: 'category',
                  boundaryGap: false,
                  data: chartData?.value?.xData
                },
                series: chartData?.value?.series,
                yAxis: {
                  name: chartData?.value?.units,
                  type: 'value',
                  axisLine: {
                    show: true
                  },
                  splitNumber: 4,
                  axisLabel: {
                    margin: 5,
                    formatter: function (value) {
                      if (value === 0) {
                        return 0
                      }
                      return handleUnit(value, chartData?.value?.units, true)
                    }
                  }
                },
                dataZoom:
                  chartData?.value?.xData?.length > 20
                    ? [
                        {
                          type: 'inside',
                          start: 0,
                          end: 100
                        },
                        {
                          start: 0,
                          end: 100
                        }
                      ]
                    : [],
                legend: {
                  right: '10px',
                  top: '7px',
                  type: 'scroll',
                  width: '80%'
                },
                tooltip: {
                  trigger: 'axis',
                  enterable: true,
                  position: function (point, params, dom, rect, size) {
                    const { contentSize, viewSize } = size
                    let x, y
                    // x
                    if (point[0] > viewSize[0] - contentSize[0]) {
                      x = point[0] - contentSize[0]
                    } else {
                      x = point[0]
                    }
                    // y
                    if (point[1] > viewSize[1] - contentSize[1]) {
                      y = point[1] - contentSize[1]
                    } else {
                      y = point[1]
                    }
                    x = x < 0 ? 0 : x
                    return [x, y]
                  },
                  formatter: function (params) {
                    return tooltipFormatter(params, chartData?.value?.units)
                  }
                },
                grid: {
                  left: '20px',
                  right: '35px',
                  top: '30px',
                  bottom: '50px',
                  containLabel: true
                }
              }}
            />
          ) : (
            <NoDataComponent />
          )}
        </div>
      </Dialog>

      {/* 查看屏蔽规则 */}
      <ViewDrawer
        visible={!!shieldVersion}
        shieldVersion={shieldVersion}
        onClose={() => {
          setShieldVersion('')
        }}
      />
    </div>
  )
}

export default styled(TacticsList)`
  .header {
    display: flex;
    justify-content: space-between;
  }
  .text-overflow {
    margin-bottom: 0;
  }
  .chart-content {
    height: 500px;
  }
  .table_instance {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    span {
      max-width: 80%;
      word-break: break-all;
    }
  }
  .thead-action {
    display: flex;
    align-items: center;
    i {
      margin-left: 5px;
    }
  }
`
